import { PRO_PAGE_ROUTE_PER_PRO_TYPE } from '@application/constants';
import {
  formatDistance,
  formatLanguages,
  translatedFieldWithFallback,
  useRouter,
} from '@application/utils';
import type { ProsListGetProsQuery } from '@gql/graphql';
import { ProfessionalTypeEnum } from '@gql/graphql';
import type { CardItemProData } from '@presentation/components';
import { PLACEHOLDER_IMAGE, PLACEHOLDER_IMAGE_USER } from '@presentation/constants';
import { BREAKPOINTS } from '@presentation/styles';
import { useCallback } from 'react';
import { useMediaQuery } from 'react-responsive';

export const useFormatPros = () => {
  const { routeLocationDescriptor, language, infoFromCurrentPath } = useRouter();

  const isMobile = useMediaQuery({ query: `(max-width: ${BREAKPOINTS.phone}px)` });
  const preloadImagesCount = isMobile ? 1 : 2;

  const formatPros = useCallback(
    (pros: ProsListGetProsQuery['searchProfessionals']['professionals']): CardItemProData[] => {
      return pros.map((pro, index) => {
        const url = translatedFieldWithFallback(language, pro?.translations, 'url');

        if (!url) {
          // eslint-disable-next-line no-console
          console.warn('No url for pro', pro);
        }
        const link = routeLocationDescriptor(PRO_PAGE_ROUTE_PER_PRO_TYPE[pro.type], {
          params: [url ?? ''],
          state: {
            returnSearch: infoFromCurrentPath?.search,
            returnParams: infoFromCurrentPath?.params,
          },
        });
        const lazyLoadImage = index >= preloadImagesCount;

        const minumumData: CardItemProData = {
          isUnclaimed: pro.unclaimedProfile,
          location: pro.address?.city ?? undefined,
          name: translatedFieldWithFallback(language, pro?.translations, 'title') ?? '-',
          distance: pro.distance ? formatDistance(pro.distance) : undefined,
          link,
          lazyLoadImage,
        };

        if (pro.type === ProfessionalTypeEnum.EDUCATOR) {
          return {
            ...minumumData,
            languages: formatLanguages(language, pro?.spokenLanguages ?? []),
            image: pro.imgs[0]?.small ?? PLACEHOLDER_IMAGE_USER,
            description: translatedFieldWithFallback(language, pro?.translations, 'description'),
            reviewsList: pro.reviews?.length
              ? {
                  amount: pro.reviews?.length,
                  average:
                    pro.reviews.reduce((acc, curr) => acc + (curr?.grade ?? 0), 0) /
                    pro.reviews?.length,
                }
              : undefined,
          } as CardItemProData;
        }
        if (pro.unclaimedProfile) {
          return {
            ...minumumData,
            image: PLACEHOLDER_IMAGE,
          } as CardItemProData;
        }

        return {
          ...minumumData,
          languages: formatLanguages(language, pro?.spokenLanguages ?? []),
          hasBooking: pro.onlineBookingEnabled,
          image: pro.imgs[0]?.small ?? PLACEHOLDER_IMAGE_USER,
          description: translatedFieldWithFallback(language, pro?.translations, 'description'),
        } as CardItemProData;
      });
    },
    [
      infoFromCurrentPath?.params,
      infoFromCurrentPath?.search,
      language,
      preloadImagesCount,
      routeLocationDescriptor,
    ],
  );

  const getImagesToPreload = useCallback(
    (pros: CardItemProData[]): string[] => {
      return pros.slice(0, preloadImagesCount).map((pro) => pro.image as string);
    },
    [preloadImagesCount],
  );

  return { formatPros, getImagesToPreload };
};
